import { DataFrame } from '../model/DataFrame';
import { readSheet } from 'read-excel-file/browser';
/**
 * Split CSV text into logical records, respecting quoted fields that may
 * contain newlines. Returns an array of record strings (one per CSV row).
 */
function splitCsvRecords(text) {
    const records = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < text.length; i++) {
        const char = text[i];
        if (inQuotes) {
            if (char === '"') {
                if (i + 1 < text.length && text[i + 1] === '"') {
                    // Escaped quote
                    current += '""';
                    i++;
                }
                else {
                    current += '"';
                    inQuotes = false;
                }
            }
            else {
                current += char;
            }
        }
        else if (char === '"') {
            current += '"';
            inQuotes = true;
        }
        else if (char === '\r') {
            // Handle \r\n or standalone \r as record delimiter
            if (i + 1 < text.length && text[i + 1] === '\n') {
                i++;
            }
            const trimmed = current.trim();
            if (trimmed.length > 0) {
                records.push(trimmed);
            }
            current = '';
        }
        else if (char === '\n') {
            const trimmed = current.trim();
            if (trimmed.length > 0) {
                records.push(trimmed);
            }
            current = '';
        }
        else {
            current += char;
        }
    }
    const trimmed = current.trim();
    if (trimmed.length > 0) {
        records.push(trimmed);
    }
    return records;
}
/**
 * Parse a single CSV line respecting quoted fields.
 * Handles separators inside quotes and escaped quotes (`""`).
 */
function parseCsvLine(line, separator) {
    const fields = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        if (inQuotes) {
            if (char === '"') {
                // Escaped quote ("") → literal quote
                if (i + 1 < line.length && line[i + 1] === '"') {
                    current += '"';
                    i++;
                }
                else {
                    // Closing quote
                    inQuotes = false;
                }
            }
            else {
                current += char;
            }
        }
        else if (char === '"') {
            inQuotes = true;
        }
        else if (char === separator) {
            fields.push(current);
            current = '';
        }
        else {
            current += char;
        }
    }
    fields.push(current);
    return fields;
}
function detectSeparator(headerLine) {
    const separators = [',', ';', '|'];
    let maxCount = 0;
    let detected = separators[0];
    for (const sep of separators) {
        const count = parseCsvLine(headerLine, sep).length;
        if (count > maxCount) {
            maxCount = count;
            detected = sep;
        }
    }
    return detected || ',';
}
export async function getDataFrameFromCSV(csvFile) {
    const text = await csvFile.text();
    // Split into logical CSV records (handles multiline quoted fields)
    const lines = splitCsvRecords(text);
    if (lines.length === 0) {
        throw new Error('CSV file is empty');
    }
    // Detect separator
    if (!lines[0]) {
        throw new Error('CSV header line is empty');
    }
    const separator = detectSeparator(lines[0]);
    // Parse header row
    const header = parseCsvLine(lines[0], separator).map((h) => h.trim());
    // Build column metadata
    const columns = header.map((name) => ({
        name,
        alias: name,
        id: false,
        type: 'string', // updated later below
    }));
    // Parse row data
    const rows = lines.slice(1).map((line) => {
        const values = parseCsvLine(line, separator);
        const row = {};
        header.forEach((colName, i) => {
            let v = values[i];
            // Try infer numeric
            const num = Number(v);
            if (!isNaN(num) && v.trim() !== '') {
                row[colName] = num;
            }
            else {
                row[colName] = v;
            }
        });
        return row;
    });
    // Infer column types by checking multiple rows
    columns.forEach((col) => {
        const samplesToCheck = Math.min(10, rows.length);
        let numericCount = 0;
        for (let i = 0; i < samplesToCheck; i++) {
            const value = rows[i]?.[col.name];
            if (typeof value === 'number') {
                numericCount++;
            }
        }
        if (numericCount > samplesToCheck / 2) {
            col.type = 'number';
        }
        else {
            col.type = 'string';
        }
    });
    return new DataFrame(columns, rows);
}
/**
 * Column names come from the first row of a workbook the user picked, and the
 * rows below are plain objects keyed by those names. Blank cells get a
 * positional name, duplicates get a numeric suffix, and names that would reach
 * the prototype chain are replaced rather than assigned.
 */
export function toColumnNames(headerRow) {
    const forbidden = new Set(['__proto__', 'constructor', 'prototype']);
    const seen = new Set();
    return headerRow.map((cell, index) => {
        // Names are kept verbatim (no trimming) so that a workbook re-imported
        // into an existing network still matches fields created from an earlier
        // import of the same file.
        let name = cell === null || cell === undefined ? '' : String(cell);
        if (name.trim() === '' || forbidden.has(name)) {
            name = `column_${index + 1}`;
        }
        let unique = name;
        let suffix = 1;
        while (seen.has(unique)) {
            unique = `${name}_${suffix++}`;
        }
        seen.add(unique);
        return unique;
    });
}
/** A row every cell of which is empty carries no data. Excel leaves these
 *  behind freely (a separator row, a cleared line), and they used to be
 *  dropped by the previous parser — keeping them would create blank POIs on
 *  import. A cell holding only whitespace counts as empty, in line with the
 *  trimming done by `normaliseExcelValue`. A row with at least one filled
 *  cell is kept as-is. */
function isBlankRow(values) {
    return values.every((v) => v === null ||
        v === undefined ||
        (typeof v === 'string' && v.trim() === ''));
}
/**
 * Cell values arrive natively typed from the parser. Dates become ISO day
 * strings — the previous parser handed back raw Excel serial numbers, which
 * were unusable downstream. Text is trimmed: imported values end up stored as
 * typed, and surrounding whitespace breaks sorting, search and de-duplication
 * further down. Booleans collapse to 1/0 and numeric-looking text becomes a
 * number, as before.
 */
function normaliseExcelValue(value) {
    if (value === null || value === undefined)
        return '';
    if (typeof value === 'number')
        return value;
    if (typeof value === 'boolean')
        return value ? 1 : 0;
    if (value instanceof Date)
        return value.toISOString().slice(0, 10);
    const text = String(value).trim();
    const num = Number(text);
    return !isNaN(num) && text !== '' ? num : text;
}
export async function getDataFrameFromExcel(excelFile) {
    // Read the file as an ArrayBuffer
    const arrayBuffer = await excelFile.arrayBuffer();
    // Parse the first sheet into rows of natively-typed cell values. The
    // parser's own `trim` is off because it would also trim the header row:
    // column names have to stay verbatim so a workbook re-imported into an
    // existing network still auto-matches its fields. Values are trimmed
    // separately, in `normaliseExcelValue`.
    const sheet = await readSheet(arrayBuffer, { trim: false });
    // The first row carries the column names, so a sheet with nothing below it
    // holds no data.
    const dataRows = sheet.slice(1).filter((values) => !isBlankRow(values));
    if (dataRows.length === 0) {
        throw new Error('Excel sheet is empty');
    }
    // Get header names from the first row
    const header = toColumnNames(sheet[0]);
    // Build column metadata
    const columns = header.map((name, index) => ({
        name,
        alias: name,
        id: index === 0, // first column becomes ID
        type: 'string', // will infer below
    }));
    // Parse row data and infer numeric types
    const rows = dataRows.map((values) => {
        const parsedRow = {};
        header.forEach((colName, index) => {
            parsedRow[colName] = normaliseExcelValue(values[index]);
        });
        return parsedRow;
    });
    // Infer column types by checking multiple rows
    columns.forEach((col) => {
        const samplesToCheck = Math.min(10, rows.length);
        let numericCount = 0;
        for (let i = 0; i < samplesToCheck; i++) {
            const value = rows[i]?.[col.name];
            if (typeof value === 'number') {
                numericCount++;
            }
        }
        if (numericCount > samplesToCheck / 2) {
            col.type = 'number';
        }
        else {
            col.type = 'string';
        }
    });
    return new DataFrame(columns, rows);
}
//# sourceMappingURL=file.js.map